import { useCallback, useMemo } from 'react';
import { getBrowserLogger } from '../../browser-logger';
import { getSessionTrackingKey } from '../../browser-store';
import { ActionType, ItemType } from '../../types/messaging-types';
import { getEncryptedConversationId } from '../utils/conversationIdUtils';
import { isTransientConversationId, type SdkLoggingFunctions } from '@indeed/messaging-react';

type CustomLogOpts = {
    additionalParams?: Record<string, unknown>;
    elementIndex?: string | number;
    itemType?: string;
    itemsAttributes?: Record<string, unknown>;
    tags?: {
        itemUids?: string[];
        [key: string]: unknown;
    };
};

export type LoggingFunctionsHelpers = Required<SdkLoggingFunctions> & {
    atk: string;
};

const LOGGING_ATK_NOT_FOUND = 'MSGW_ATK_NOT_FOUND';

// Logging param keys. conversationId/conversationIds are raw (disallowed) inputs;
// conversationSid is the allowed, encrypted field we emit in their place.
const CONVERSATION_ID_PARAM = 'conversationId';
const CONVERSATION_IDS_PARAM = 'conversationIds';
const CONVERSATION_SID_PARAM = 'conversationSid';
const EVENT_ID_PARAM = 'eventId';
const MESSAGE_ID_PARAM = 'messageId';

/**
 * Builds additionalParams from CustomLogOpts
 * Only returns additionalParams if explicitly provided
 * Encrypts conversationId values if present
 */
const buildAdditionalParams = (options?: CustomLogOpts): Record<string, unknown> | undefined => {
    if (!options || options.additionalParams === undefined) {
        return undefined;
    }

    const params = options.additionalParams as Record<string, unknown>;

    // conversationId is NOT an allowed userAction field, so it must never reach the
    // logging endpoint. Strip it whenever the key is present and re-emit it as the
    // allowed, encrypted conversationSid only when it is a real, non-empty string.
    // Empty or non-string values (e.g. the `response?.conversationId || ''`
    // fallbacks in the SDK) are dropped entirely — leaking them as a raw
    // conversationId field is what produced the 400 "Field conversationId is not an
    // allowed field" errors (COMMSEXP-640).
    if (CONVERSATION_ID_PARAM in params) {
        const { [CONVERSATION_ID_PARAM]: conversationId, ...rest } = params;
        const trimmedConversationId =
            typeof conversationId === 'string' ? conversationId.trim() : '';
        // Transient (not-yet-persisted) conversations have no real id to encrypt. Omit the
        // sid entirely rather than wrapping the sentinel, which Conversation Service would
        // reject as a "malformed or incorrect encrypted id" (COMMSEXP-654).
        if (trimmedConversationId.length > 0 && !isTransientConversationId(trimmedConversationId)) {
            return buildAdditionalParams({
                ...options,
                additionalParams: {
                    ...rest,
                    [CONVERSATION_SID_PARAM]: getEncryptedConversationId(trimmedConversationId),
                },
            });
        }
        // Nothing left to log once a bare/invalid/transient conversationId is removed: omit
        // additionalParams entirely rather than attaching an empty object.
        return buildAdditionalParams({
            ...options,
            additionalParams: rest,
        });
    }

    if (EVENT_ID_PARAM in params) {
        const { [EVENT_ID_PARAM]: eventId, ...rest } = params;

        return MESSAGE_ID_PARAM in rest
            ? rest
            : {
                  ...rest,
                  [MESSAGE_ID_PARAM]: eventId,
              };
    }

    return Object.keys(params).length > 0 ? params : undefined;
};

/**
 * Hook that provides logging functions for the new logging architecture.
 */
const useLoggingFunctions = (): LoggingFunctionsHelpers => {
    // Get browser logger (may fail in Storybook/test environments)
    let browserLogger: {
        logUserAction: (log: any) => void;
        logImpression: (log: any) => void;
        logCaughtError: (e: Error) => void;
    };
    try {
        browserLogger = getBrowserLogger();
    } catch (e) {
        console.warn('Browser logger not initialized. Using no-op logger.', e);
        browserLogger = {
            logUserAction: () => {},
            logImpression: () => {},
            logCaughtError: () => {},
        };
    }

    const getElementString = (element?: string, elementIndex?: string | number): string => {
        if (!element) {
            return '';
        }
        const indexString = elementIndex == null ? '' : `[${elementIndex}]`;
        return `'${element}${indexString}'`;
    };

    // Get pageload tracking key
    let pageloadTk = '';
    try {
        pageloadTk = getSessionTrackingKey().pageloadTk;
    } catch (e) {
        console.warn('Browser store not initialized. Falling back to default atk.', e);
        pageloadTk = '';
    }

    const logClick = useCallback(
        (element: string, options?: Record<string, unknown>) => {
            const opts = options as CustomLogOpts | undefined;
            const additionalParams = buildAdditionalParams(opts);
            const logPayload: any = {
                action: ActionType.CLICK,
                itemUid: getElementString(element, opts?.elementIndex),
                itemType: ItemType.BUTTON,
            };

            if (additionalParams !== undefined) {
                logPayload.additionalParams = additionalParams;
            }

            browserLogger.logUserAction(logPayload);
        },
        [browserLogger]
    );

    const logImpression = useCallback(
        (element: string, options?: Record<string, unknown>) => {
            const opts = options as CustomLogOpts | undefined;
            // Extract and encrypt conversationIds if present in additionalParams
            let conversationSids: string[] | undefined;
            let additionalParamsToUse = opts?.additionalParams as
                | Record<string, unknown>
                | undefined;

            if (
                additionalParamsToUse &&
                CONVERSATION_IDS_PARAM in additionalParamsToUse &&
                Array.isArray(additionalParamsToUse[CONVERSATION_IDS_PARAM])
            ) {
                const { [CONVERSATION_IDS_PARAM]: conversationIds, ...rest } =
                    additionalParamsToUse;
                // Drop transient (not-yet-persisted) ids before encrypting; wrapping the
                // sentinel produces a sid Conversation Service rejects as "malformed or
                // incorrect encrypted id" (COMMSEXP-654).
                const encryptableIds = (conversationIds as string[]).filter(
                    (id) => !isTransientConversationId(id)
                );
                conversationSids =
                    encryptableIds.length > 0
                        ? encryptableIds.map((id) => getEncryptedConversationId(id))
                        : undefined;
                additionalParamsToUse = Object.keys(rest).length > 0 ? rest : undefined;
            }

            const additionalParams = buildAdditionalParams({
                ...opts,
                additionalParams: additionalParamsToUse,
            });

            const logPayload: any = {
                itemUids: opts?.tags?.itemUids ?? conversationSids ?? [element],
                itemType: opts?.itemType ?? element ?? ItemType.MESSAGES,
                itemsAttributes: opts?.itemsAttributes ?? {},
            };

            if (additionalParams !== undefined) {
                logPayload.additionalParams = additionalParams;
            }

            browserLogger.logImpression(logPayload);
        },
        [browserLogger]
    );

    const logCustomAction = useCallback(
        (customActionType: string, element: string, options?: Record<string, unknown>) => {
            const opts = options as CustomLogOpts | undefined;
            const additionalParams = buildAdditionalParams(opts);

            const logPayload: any = {
                action: customActionType,
                itemUid: opts?.elementIndex ?? element,
                itemType: opts?.itemType ?? element,
            };

            if (additionalParams !== undefined) {
                logPayload.additionalParams = additionalParams;
            }

            browserLogger.logUserAction(logPayload);
        },
        [browserLogger]
    );

    const logFailure = useCallback(
        (element: string, errorOptions?: Record<string, unknown>) => {
            const errorMessage =
                errorOptions?.error != null
                    ? `${element} - ${String(errorOptions.error)}`
                    : element;
            browserLogger.logCaughtError(new Error(errorMessage));
        },
        [browserLogger]
    );

    return useMemo(
        () => ({
            atk: pageloadTk || LOGGING_ATK_NOT_FOUND,
            logClick,
            logImpression,
            logCustomAction,
            logFailure,
        }),
        [pageloadTk, logClick, logImpression, logCustomAction, logFailure]
    );
};

export default useLoggingFunctions;
